 

import {Action, action, Thunk, thunk} from 'easy-peasy';
import {capitalizeFirstLetter} from './text';
import {StoreModel} from '@/store';


interface PaginatedAPIResponse<Entity extends object = {}> {
    items: Entity[];
    pages: number[];
    count: number;
}

interface BaseEntity {
    id: number;
}

export interface SimpleCrudStoreModel<Entity extends object = BaseEntity> {
    items: Entity[];
    store: Action<SimpleCrudStoreModel, Entity[]>;

    addOrEdit: Action<SimpleCrudStoreModel, Entity>;
    deleteOne: Action<SimpleCrudStoreModel, number>;

    loading: boolean;
    getLoadingLock: Thunk<SimpleCrudStoreModel, boolean, null, StoreModel, boolean>;
    setLoading: Action<SimpleCrudStoreModel, boolean>;

    storePaginatedData: Thunk<SimpleCrudStoreModel, PaginatedAPIResponse<Entity>>;

    activeItem: null | Entity;
    setActiveItem: Action<SimpleCrudStoreModel, Entity>;

    paginationPages: null | number[];
    paginationItemCount: null | number;
    paginationCurrentPage: null | number;
    setPaginationPages: Action<SimpleCrudStoreModel, number[]>;
    setPaginationItemCount: Action<SimpleCrudStoreModel, number>;
    setPaginationCurrentPage: Action<SimpleCrudStoreModel, number>;

    reload: boolean;
    setReload: Action<SimpleCrudStoreModel, boolean>;
}

export const simpleCrudStoreFactory = (): SimpleCrudStoreModel => ({
    items: [],
    store: action((state, payload) => {
        state.items = payload;
    }),

    addOrEdit: action((state, newObj) => {
        if (!Array.isArray(state.items)) {
            state.items = [];
        }
        const existingIndex = state.items.findIndex((el) => el.id === newObj.id);

        if (existingIndex !== -1) {
            state.items.splice(existingIndex, 1);
        }

        state.items.unshift(newObj);
    }),
    deleteOne: action((state, id) => {
        if (!Array.isArray(state.items)) {
            state.items = [];
            return;
        }
        const index = state.items.findIndex((el) => el.id === id);

        if (index !== -1) {
            state.items.splice(index, 1);
        }
    }),

    loading: false,
    getLoadingLock: thunk((actions, loading, {getState}) => {
        if (getState().loading && loading) {
            return false;
        } else {
            actions.setLoading(loading);
            return true;
        }
    }),
    setLoading: action((state, loading) => {
        state.loading = loading;
    }),

    storePaginatedData: thunk((actions, payload) => {
        actions.store(payload.items);
        actions.setPaginationPages(payload.pages);
        actions.setPaginationItemCount(payload.count);
    }),

    activeItem: null,
    setActiveItem: action((state, activeItem) => {
        state.activeItem = activeItem;
    }),

    paginationPages: null,
    paginationItemCount: null,
    paginationCurrentPage: null,
    setPaginationPages: action((state, payload) => {
        state.paginationPages = payload;
    }),
    setPaginationItemCount: action((state, payload) => {
        state.paginationItemCount = payload;
    }),
    setPaginationCurrentPage: action((state, payload) => {
        state.paginationCurrentPage = payload;
    }),

    reload: false,
    setReload: action((state, value) => {
        state.reload = value;
    }),
});

export type SimpleBooleanStoreModel<Name extends string> = {
    [key in Name | `set${Capitalize<Name>}`]:
        key extends Name ? boolean : Action<SimpleBooleanStoreModel<Name>, boolean>;
};

export const simpleBooleanStoreFactory = <Name extends string>(name: Name, defaultValue?: boolean):
    SimpleBooleanStoreModel<Name> => <SimpleBooleanStoreModel<Name>>(
    {
        [name]: typeof defaultValue === 'undefined' ? false : defaultValue,
        [`set${capitalizeFirstLetter(name)}`]: action((state, value) => {
            // @ts-ignore
            state[name] = value;
        }
        ),
    }
);
